/**
 * Map Tab Content Component
 * Renders the map tab content for a response pair
 * Shows experiences from the current conversation with color coding
 */

import React, { useMemo, useEffect, useState, useRef } from 'react';
import { ExperienceCard } from '@/constants/chatScreenData';
import { ChatLocation } from '@/types/chatTypes';
import { MapView, MapPlace } from '../../map';
import { getPriceDisplay } from '@/utils/experienceMapUtils';
import { ExtendedExperienceCard } from '@/constants/experiencesData';
import { useGoogleMapsLoader } from '@/hooks/useGoogleMapsLoader';
import { spacing } from '@/design-system/utils';

export interface MapTabContentProps {
  isActive: boolean;
  experiences: ExperienceCard[]; // Experiences from the current conversation
  locations?: ChatLocation[]; // All locations including businesses (for map display)
  onCardClick?: (cardId: string) => void;
}

export const MapTabContent: React.FC<MapTabContentProps> = ({
  isActive,
  experiences,
  locations = [],
  onCardClick,
}) => {
  const [geocodedPlaces, setGeocodedPlaces] = useState<MapPlace[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  // Track the last experiences we geocoded to avoid re-geocoding
  const lastGeocodedExperienceIdsRef = useRef<Set<string>>(new Set());
  // Track the last locations we processed
  const lastProcessedLocationIdsRef = useRef<Set<string>>(new Set());
  // Ref to track if map has been loaded at least once
  const mapLoadedRef = useRef(false);
  // Ref to track the previous experiences array to detect changes
  const previousExperiencesRef = useRef<ExperienceCard[]>([]);
  // Ref to track the previous locations array to detect changes
  const previousLocationsRef = useRef<ChatLocation[]>([]);
  // Refs to track processed keys for stable comparison
  const lastExperiencesKeyRef = useRef<string>('');
  const lastLocationsKeyRef = useRef<string>('');
  // Refs to store latest values to avoid including arrays in dependency array
  const extendedExperiencesRef = useRef<ExtendedExperienceCard[]>([]);
  const validLocationsForMapRef = useRef<ChatLocation[]>([]);

  // Load Google Maps API
  const { isLoaded: isMapsLoaded, loadError: mapsLoadError } =
    useGoogleMapsLoader();

  // Convert experiences to ExtendedExperienceCard format for price display
  const extendedExperiences = useMemo((): ExtendedExperienceCard[] => {
    const mapped = experiences.map(exp => {
      const mappedCard = {
        id: exp.id,
        title: exp.title,
        image: exp.image,
        images: exp.images,
        location: exp.location,
        type: exp.type,
        slug: exp.slug,
        rating: exp.rating,
        ratingCount: exp.ratingCount,
        description: exp.description,
        price: exp.price,
        currencySymbol: exp.currencySymbol,
        duration: exp.duration,
        attractionsCount: exp.attractionsCount,
        days: exp.days,
        nights: exp.nights,
        locationsCount: exp.locationsCount,
        tags: exp.tags,
        // Include coordinates if available
        latitude: exp.latitude,
        longitude: exp.longitude,
      };

      return mappedCard;
    });

    return mapped;
  }, [experiences]);

  // Filter locations to exclude pure administrative areas only
  const validLocationsForMap = useMemo(() => {
    if (!locations || locations.length === 0) {
      return [];
    }

    // Filter valid locations - show all locations that have place_id and a name
    const validLocations = locations.filter(location => {
      if (
        !location.place_id ||
        !(location.title || location.place || location.place_name)
      ) {
        return false;
      }

      return true;
    });

    // Deduplicate by place_id + place_name combination
    // This ensures locations with same place_id but different names are shown separately
    const seenIds = new Set<string>();
    return validLocations.filter(location => {
      const name =
        location.title || location.place || location.place_name || '';
      const id = location.place_id ? `${location.place_id}_${name}` : name;
      if (seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);
      return true;
    });
  }, [locations]);

  // Update refs when values change (after useMemo declarations)
  useEffect(() => {
    const prevExpCount = extendedExperiencesRef.current.length;
    const prevLocCount = validLocationsForMapRef.current.length;

    extendedExperiencesRef.current = extendedExperiences;
    validLocationsForMapRef.current = validLocationsForMap;

    if (
      prevExpCount !== extendedExperiences.length ||
      prevLocCount !== validLocationsForMap.length
    ) {
      console.log('🔄 [MapTabContent] 📝 Refs updated:', {
        experiences: {
          previous: prevExpCount,
          current: extendedExperiences.length,
          changed: prevExpCount !== extendedExperiences.length,
        },
        locations: {
          previous: prevLocCount,
          current: validLocationsForMap.length,
          changed: prevLocCount !== validLocationsForMap.length,
        },
      });
    }
  }, [extendedExperiences, validLocationsForMap]);

  // Clear state when experiences or locations change (different conversation)
  useEffect(() => {
    // Check if experiences have actually changed (different conversation)
    const currentExperienceIds = new Set(experiences.map(exp => exp.id).sort());
    const previousExperienceIds = new Set(
      previousExperiencesRef.current.map(exp => exp.id).sort()
    );

    // Check if locations have changed
    const currentLocationIds = new Set(
      locations.map(loc => loc.place_id || loc.title || loc.place).sort()
    );
    const previousLocationIds = new Set(
      previousLocationsRef.current
        .map(loc => loc.place_id || loc.title || loc.place)
        .sort()
    );

    // Create sorted arrays for comparison
    const currentExpIdsArray = Array.from(currentExperienceIds).sort();
    const previousExpIdsArray = Array.from(previousExperienceIds).sort();
    const currentLocIdsArray = Array.from(currentLocationIds).sort();
    const previousLocIdsArray = Array.from(previousLocationIds).sort();

    // Compare by IDs to detect if we're looking at a different conversation
    const experiencesChanged =
      currentExpIdsArray.length !== previousExpIdsArray.length ||
      !currentExpIdsArray.every((id, idx) => id === previousExpIdsArray[idx]);

    const locationsChanged =
      currentLocIdsArray.length !== previousLocIdsArray.length ||
      !currentLocIdsArray.every((id, idx) => id === previousLocIdsArray[idx]);

    if (experiencesChanged || locationsChanged) {
      // Experiences or locations changed - clear state and reset refs
      setGeocodedPlaces([]);
      setIsGeocoding(false);
      lastGeocodedExperienceIdsRef.current = new Set();
      lastProcessedLocationIdsRef.current = new Set();
      mapLoadedRef.current = false;
    }

    // Update the refs to track current data
    previousExperiencesRef.current = [...experiences];
    previousLocationsRef.current = [...locations];
  }, [experiences, locations]);

  // Create stable string keys for experiences and locations to avoid infinite loops
  const experiencesKey = useMemo(
    () =>
      experiences
        .map(exp => exp.id)
        .sort()
        .join(','),
    [experiences]
  );
  const locationsKey = useMemo(
    () =>
      validLocationsForMap
        .map(loc => loc.place_id || `${loc.title || loc.place}`)
        .sort()
        .join(','),
    [validLocationsForMap]
  );

  // Process experiences and locations to create map places
  useEffect(() => {
    console.log('🔄 [MapTabContent] useEffect triggered:', {
      isActive,
      experiencesKey,
      locationsKey,
      experiencesCount: extendedExperiencesRef.current.length,
      locationsCount: validLocationsForMapRef.current.length,
      geocodedPlacesCount: geocodedPlaces.length,
      lastExperiencesKey: lastExperiencesKeyRef.current,
      lastLocationsKey: lastLocationsKeyRef.current,
      isMapsLoaded,
      mapsLoadError: !!mapsLoadError,
    });

    // Don't process if tab is not active
    if (!isActive) {
      console.log('🔄 [MapTabContent] ⏸️ Tab not active, skipping processing');
      // Don't clear places when tab becomes inactive - keep them for when user switches back
      return;
    }

    // Get latest values from refs to avoid dependency issues
    const currentExtendedExperiences = extendedExperiencesRef.current;
    const currentValidLocationsForMap = validLocationsForMapRef.current;

    // If no experiences and no locations, clear places to show empty map state
    if (
      currentExtendedExperiences.length === 0 &&
      currentValidLocationsForMap.length === 0
    ) {
      console.log(
        '🔄 [MapTabContent] 📭 No experiences or locations, clearing places'
      );
      setGeocodedPlaces([]);
      setIsGeocoding(false);
      return;
    }

    // Wait for Google Maps API to be loaded before processing
    if (!isMapsLoaded) {
      console.log('🔄 [MapTabContent] ⏳ Waiting for Google Maps API to load');
      return;
    }

    if (mapsLoadError) {
      console.error(
        '🔄 [MapTabContent] ❌ Google Maps API failed to load:',
        mapsLoadError
      );
      setGeocodedPlaces([]);
      return;
    }

    // Check if we already processed these experiences and locations using stable keys
    const currentExperiencesKey = experiencesKey;
    const currentLocationsKey = locationsKey;

    // If we already processed these exact experiences and locations, skip
    if (
      currentExperiencesKey === lastExperiencesKeyRef.current &&
      currentLocationsKey === lastLocationsKeyRef.current &&
      geocodedPlaces.length > 0
    ) {
      console.log(
        '🔄 [MapTabContent] ⚠️ Already processed - skipping (preventing infinite loop):',
        {
          currentExperiencesKey,
          currentLocationsKey,
          geocodedPlacesCount: geocodedPlaces.length,
        }
      );
      return;
    }

    console.log(
      '🔄 [MapTabContent] 🚀 Processing new experiences and locations:',
      {
        experiencesCount: currentExtendedExperiences.length,
        locationsCount: currentValidLocationsForMap.length,
        experiencesKey: currentExperiencesKey,
        locationsKey: currentLocationsKey,
      }
    );

    // Create AbortController for cancellation
    const abortController = new AbortController();

    // Process experiences and locations
    const processPlaces = async () => {
      setIsGeocoding(true);
      const places: MapPlace[] = [];

      // Process experiences
      for (let i = 0; i < currentExtendedExperiences.length; i++) {
        // Check abort signal
        if (abortController.signal.aborted) {
          break;
        }

        const experience = currentExtendedExperiences[i];

        // Use coordinates directly from experience (already extracted by mapper)
        // ExperienceCard already has latitude/longitude from mapChatExperienceToCard
        const lat = experience.latitude;
        const lng = experience.longitude;

        // Skip experiences without coordinates - do not geocode
        if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
          console.warn(
            '📍 [MapTabContent] ❌ Skipping experience - no coordinates found:',
            {
              experienceId: experience.id,
              experienceTitle: experience.title,
              latitude: lat,
              longitude: lng,
              hasLat: experience.latitude !== undefined,
              hasLng: experience.longitude !== undefined,
            }
          );
          continue;
        }

        console.log('📍 [MapTabContent] ✅ Experience has coordinates:', {
          experienceId: experience.id,
          experienceTitle: experience.title,
          coordinates: { lat, lng },
        });

        // Only create place if we have valid coordinates
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          // Get price display with currency conversion (same logic as GridExperienceCard)
          // Uses getPriceDisplay which matches CurrencyDisplay component logic
          const price = getPriceDisplay(experience);

          const place: MapPlace = {
            id: experience.id || `exp-${Math.random()}`,
            title: experience.title || 'Untitled Experience',
            latitude: lat,
            longitude: lng,
            // Location field: matches experience tab (comes from exp.place in ChatExperience)
            address: experience.location,
            image: experience.image,
            rating: experience.rating,
            // Type field: matches experience tab (already mapped via mapExperienceType)
            // Values: 'activities' | 'package' | 'stays' | 'signature' | 'riders-corner'
            // ExperienceMarker's getExperienceBadgeOffsetColorCSS handles these correctly
            experienceType: experience.type,
            isExperience: true,
            // Price: formatted with currency conversion (same as GridExperienceCard)
            price: price,
          };

          places.push(place);
        }
      }

      // Process locations (ChatLocation objects already have coordinates - NO geocoding needed)
      // ChatLocation interface requires latitude and longitude fields
      // If coordinates are not available, skip this location (do NOT geocode)
      currentValidLocationsForMap.forEach((location, locationIndex) => {
        if (abortController.signal.aborted) {
          return;
        }

        // Use existing coordinates directly - skip if not available (do NOT geocode)
        const lat = location.latitude;
        const lng = location.longitude;

        if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
          console.warn(
            '📍 [MapTabContent] ❌ Skipping location - no coordinates found:',
            {
              locationId: location.place_id,
              locationTitle:
                location.title || location.place || location.place_name,
              latitude: lat,
              longitude: lng,
              hasLat: location.latitude !== undefined,
              hasLng: location.longitude !== undefined,
            }
          );
          return; // Skip this location (equivalent to continue in for loop)
        }

        console.log('📍 [MapTabContent] ✅ Location has coordinates:', {
          locationId: location.place_id,
          locationTitle:
            location.title || location.place || location.place_name,
          coordinates: { lat, lng },
        });

        const place: MapPlace = {
          id: location.place_id || `loc-${Math.random()}`,
          title:
            location.title ||
            location.place ||
            location.place_name ||
            'Unknown Location',
          latitude: lat,
          longitude: lng,
          address:
            location.subtitle ||
            location.address ||
            location.locality ||
            'Location not specified',
          image: (() => {
            // Handle both image_urls (snake_case) and imageUrls (camelCase) from API
            const imageUrls =
              location.image_urls ||
              (location as unknown as { imageUrls?: string[] }).imageUrls ||
              [];

            // Filter out invalid URLs (empty strings, null, undefined)
            const validImageUrls = imageUrls.filter(
              url => url && typeof url === 'string' && url.trim().length > 0
            );

            // Use index to pick image, but validate that we have enough images
            // If index is out of bounds or no valid images, use the last one
            if (validImageUrls.length > 0) {
              if (locationIndex < validImageUrls.length) {
                // Index is valid, use image at that index
                return validImageUrls[locationIndex];
              } else {
                // Index is out of bounds, use last image in array
                return validImageUrls[validImageUrls.length - 1];
              }
            }
            return undefined;
          })(),
          rating:
            typeof location.rating === 'number' ? location.rating : undefined,
          types: location.types,
          isExperience: false,
        };

        places.push(place);
      });

      // Only update state if not aborted
      if (!abortController.signal.aborted) {
        // Update the refs to track what we just processed
        lastExperiencesKeyRef.current = currentExperiencesKey;
        lastLocationsKeyRef.current = currentLocationsKey;
        lastGeocodedExperienceIdsRef.current = new Set(
          currentExtendedExperiences.map(exp => exp.id)
        );
        lastProcessedLocationIdsRef.current = new Set(
          currentValidLocationsForMap.map(
            loc => loc.place_id || `${loc.title || loc.place}`
          )
        );
        console.log('🔄 [MapTabContent] ✅ Processing complete:', {
          placesCount: places.length,
          experiencesProcessed: currentExtendedExperiences.length,
          locationsProcessed: currentValidLocationsForMap.length,
          experiencesKey: currentExperiencesKey,
          locationsKey: currentLocationsKey,
        });
        setGeocodedPlaces(places);
        setIsGeocoding(false);
      } else {
        console.log(
          '🔄 [MapTabContent] ❌ Processing aborted, not updating state'
        );
      }
    };

    processPlaces();

    // Cleanup: abort any in-flight requests when effect re-runs or component unmounts
    return () => {
      abortController.abort();
    };
    // geocodedPlaces omitted: this effect sets it; depending on it would be circular
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, experiencesKey, locationsKey, isMapsLoaded, mapsLoadError]);

  // Trigger map resize when tab becomes active (Google Maps needs this after being hidden)
  useEffect(() => {
    if (isActive && mapLoadedRef.current && geocodedPlaces.length > 0) {
      // Small delay to ensure DOM is updated and map container is visible
      const timer = setTimeout(() => {
        // Trigger window resize event to notify Google Maps to recalculate its size
        // This is necessary when a map is shown after being hidden with display:none
        if (window.google?.maps) {
          window.dispatchEvent(new Event('resize'));
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isActive, geocodedPlaces.length]);

  // Mark map as loaded once we have places
  useEffect(() => {
    if (geocodedPlaces.length > 0) {
      mapLoadedRef.current = true;
    }
  }, [geocodedPlaces.length]);

  // Memoize MapView to prevent unnecessary re-renders
  // Always render MapView, even with empty places array, to show empty state
  const memoizedMapView = useMemo(() => {
    return <MapView places={geocodedPlaces} onPlaceClick={onCardClick} />;
  }, [geocodedPlaces, onCardClick]);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        display: isActive ? 'block' : 'none', // Hide instead of unmounting
        height: '100%',
        paddingBottom: spacing[4],
      }}
    >
      {isGeocoding &&
      geocodedPlaces.length === 0 &&
      (extendedExperiences.length > 0 || validLocationsForMap.length > 0) ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            color: '#666',
          }}
        >
          Loading map...
        </div>
      ) : (
        memoizedMapView
      )}
    </div>
  );
};
